import React from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

/**
 * Inclusive date window stored in the URL as `?start=YYYY-MM-DD&end=YYYY-MM-DD`.
 * The hook returns ISO date strings — same shape `SalesProvider` keeps — so
 * the page-level dateRange state and the URL stay trivially convertible.
 */
export type TimeFrame = {
    start: string;
    end: string;
};

export type UseTimeFrameResult = {
    /**
     * Parsed start date from `?start=`, after the validation /
     * clamp-to-today / start≤end swap normalization. `null` when the
     * URL has no usable date pair — the caller can fall back to its
     * own default.
     */
    startDate: string | null;
    endDate: string | null;
    /**
     * Writes the given range to the URL as `start` / `end` query
     * params. Uses `replace: true` so picking shortcuts doesn't bloat
     * the browser history.
     */
    setTimeFrame: (range: TimeFrame) => void;
};

const ISO_DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Validates + normalizes a `start` / `end` pair from the URL.
 *
 *   - If neither is a valid date, returns `(null, null)` so the
 *     caller falls back to its own default.
 *   - If only one is provided, uses it for both ends (single-day
 *     range).
 *   - A `start` in the future is clamped to today — there's no
 *     legitimate URL where the window begins in the future, so
 *     this is purely defensive against stale bookmarks and
 *     hand-edited URLs.
 *   - The `end` is intentionally **not** clamped: presets like
 *     "this month" / "this year" legitimately end on a future
 *     boundary (e.g. 31 May when today is 27 May), and clamping
 *     would break the URL → segment-detection round trip
 *     (?start=2026-05-01&end=2026-05-31 must keep its end so
 *     `detectShortcut` recognises it as `thisMonth`).
 *   - When start > end (e.g. after the start-only clamp pushes
 *     it past the end), the pair is swapped so order is always
 *     well-defined.
 *   - The final pair is normalized to whole days (start-of-day /
 *     end-of-day). Time-zone handling is intentionally absent
 *     today — when `TimezoneManager` lands, drop the dayjs
 *     `.tz()` calls in here.
 */
const parseTimeFrame = (
    startRaw: string | null,
    endRaw: string | null,
): { startDate: string | null; endDate: string | null } => {
    const startValid = startRaw !== null && dayjs(startRaw).isValid();
    const endValid = endRaw !== null && dayjs(endRaw).isValid();

    if (!startValid && !endValid) {
        return { startDate: null, endDate: null };
    }

    const startStr = startValid ? startRaw! : endRaw!;
    const endStr = endValid ? endRaw! : startRaw!;

    const today = dayjs();
    let startDate = dayjs(startStr, ISO_DATE_FORMAT);
    if (startDate.isAfter(today.format(ISO_DATE_FORMAT))) {
        startDate = today;
    }

    let endDate = dayjs(endStr, ISO_DATE_FORMAT);

    if (startDate.isAfter(endDate)) {
        const tmp = startDate;
        startDate = endDate;
        endDate = tmp;
    }

    startDate = startDate.startOf('day');
    endDate = endDate.endOf('day');

    return {
        startDate: startDate.format(ISO_DATE_FORMAT),
        endDate: endDate.format(ISO_DATE_FORMAT),
    };
};

/**
 * Reads `?start=` / `?end=` from the URL, returns the validated
 * range plus a setter that writes a new range back into the URL.
 * The returned strings are stable across renders when the URL values
 * don't change, so consumers can use them in effect deps without
 * re-running on every render.
 */
export const useTimeFrame = (): UseTimeFrameResult => {
    const [searchParams, setSearchParams] = useSearchParams();
    const startRaw = searchParams.get('start');
    const endRaw = searchParams.get('end');

    const { startDate, endDate } = React.useMemo(
        () => parseTimeFrame(startRaw, endRaw),
        [startRaw, endRaw],
    );

    const setTimeFrame = React.useCallback<UseTimeFrameResult['setTimeFrame']>(
        (range) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('start', range.start);
                    next.set('end', range.end);
                    return next;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );

    return { startDate, endDate, setTimeFrame };
};
